import {
  createdClientId,
  createdClientIds,
  referencedClientIds,
} from '@/services/eventDependencyService'
import { activityLabels } from '@/services/syncCopy'
import { EventType, type Event } from '@/types/event'

/**
 * Pure derivation of the activity view: raw queued events → user-recognizable items
 * ("12 photos → 'Alps 2026'", "Track 'Morning Ride'", "Changes to 'Alps 2026'").
 * No Vue imports — unit-testable in isolation.
 *
 * Grouping rules (order-preserving by first occurrence in the queue):
 * 1. PHOTO_CREATED events group per collection → "N photos".
 * 2. Each TOUR_CREATED becomes "Track '{name}'"; queued child events that (transitively)
 *    reference an entity created inside that tour fold into the same item — reusing the
 *    dependency helpers the batching engine uses, so grouping never disagrees with batching.
 * 3. Remaining events group by family: photo changes, places (POIs/waypoints), collection
 *    changes, route changes.
 * 4. Anything unmatched lands in "Other changes" — items always sum to the queue, so the
 *    badge count and the activity list can never disagree.
 */

export type ActivityItemKind =
  | 'photos'
  | 'photo-changes'
  | 'track'
  | 'places'
  | 'collection'
  | 'route-changes'
  | 'other'

export interface ActivityItem {
  /** Stable key for list rendering. */
  key: string
  kind: ActivityItemKind
  label: string
  /** Number of queued events this item represents. */
  count: number
  eventClientIds: string[]
  status: 'syncing' | 'waiting' | 'problem'
}

export interface ActivityContext {
  /** Resolve a collection clientId to its display name, when known locally. */
  resolveCollectionName?: (clientId: string) => string | undefined
  /** clientIds of events in the in-flight batch (drives the "syncing…" status). */
  inFlightEventIds?: ReadonlySet<string>
  /** clientIds of events in the last FAILED batch (drives the "can't sync yet" status). */
  failedEventIds?: ReadonlySet<string>
}

const PHOTO_CHANGE_TYPES = new Set<EventType>([
  EventType.PHOTO_UPDATED,
  EventType.PHOTO_DELETED,
  EventType.WORKSPACE_COVER_PHOTO_SET,
])

const PLACE_TYPES = new Set<EventType>([
  EventType.WORKSPACE_POI_CREATED,
  EventType.WORKSPACE_POI_UPDATED,
  EventType.WORKSPACE_POI_DELETED,
  EventType.WORKSPACE_POI_ADDED_TO_WORKSPACE,
  EventType.WORKSPACE_POI_REMOVED_FROM_WORKSPACE,
  EventType.WAYPOINT_CREATED,
  EventType.WAYPOINT_UPDATED,
  EventType.WAYPOINT_DELETED,
])

const COLLECTION_TYPES = new Set<EventType>([
  EventType.WORKSPACE_CREATED,
  EventType.WORKSPACE_UPDATED,
  EventType.WORKSPACE_DELETED,
])

const ROUTE_TYPES = new Set<EventType>([
  EventType.TOUR_ADDED_TO_WORKSPACE,
  EventType.TOUR_UPDATED,
  EventType.TOUR_DELETED,
  EventType.TOUR_REMOVED_FROM_WORKSPACE,
  EventType.TRACK_CREATED,
  EventType.TRACK_UPDATED,
  EventType.TRACK_DELETED,
  EventType.SEGMENT_CREATED,
  EventType.SEGMENT_UPDATED,
  EventType.SEGMENT_DELETED,
  EventType.ROUTE_POINT_CREATED,
  EventType.ROUTE_POINT_UPDATED,
  EventType.ROUTE_POINT_DELETED,
])

interface MutableItem {
  key: string
  kind: ActivityItemKind
  label: string
  eventClientIds: string[]
}

export function deriveActivityItems(events: Event[], ctx: ActivityContext = {}): ActivityItem[] {
  const items: MutableItem[] = []
  const itemByKey = new Map<string, MutableItem>()

  // Fold-sets of the queued TOUR_CREATED items: tour item key → created entity ids inside it.
  // Events are queued parents-first, so one ordered pass resolves transitive folding.
  const tourFoldSets: Array<{ item: MutableItem; ids: Set<string> }> = []

  const upsert = (key: string, kind: ActivityItemKind, label: string): MutableItem => {
    let item = itemByKey.get(key)
    if (!item) {
      item = { key, kind, label, eventClientIds: [] }
      itemByKey.set(key, item)
      items.push(item)
    }
    return item
  }

  for (const event of events) {
    const payload = event.payload as Record<string, unknown>

    // 2. New tour with everything inside
    if (event.type === EventType.TOUR_CREATED) {
      const tourId = createdClientId(event)
      const name = typeof payload.name === 'string' ? payload.name : undefined
      const item = upsert(`track:${tourId ?? event.clientId}`, 'track', activityLabels.track(name))
      item.eventClientIds.push(event.clientId)
      tourFoldSets.push({ item, ids: new Set(createdClientIds(event)) })
      continue
    }

    // Fold child events into the queued tour they belong to (transitive via created ids)
    const refs = referencedClientIds(event)
    const foldTarget =
      refs.length > 0
        ? tourFoldSets.find((entry) => refs.some((ref) => entry.ids.has(ref)))
        : undefined
    if (foldTarget && event.type !== EventType.PHOTO_CREATED) {
      foldTarget.item.eventClientIds.push(event.clientId)
      for (const created of createdClientIds(event)) {
        foldTarget.ids.add(created)
      }
      continue
    }

    // 1. Photos per collection
    if (event.type === EventType.PHOTO_CREATED) {
      const collectionId =
        typeof payload.workspaceClientId === 'string' ? payload.workspaceClientId : 'unknown'
      const item = upsert(`photos:${collectionId}`, 'photos', '')
      item.eventClientIds.push(event.clientId)
      continue
    }

    // 3. Family groups
    if (PHOTO_CHANGE_TYPES.has(event.type)) {
      upsert('photo-changes', 'photo-changes', '').eventClientIds.push(event.clientId)
      continue
    }
    if (PLACE_TYPES.has(event.type)) {
      upsert('places', 'places', '').eventClientIds.push(event.clientId)
      continue
    }
    if (COLLECTION_TYPES.has(event.type)) {
      const collectionId = typeof payload.clientId === 'string' ? payload.clientId : 'unknown'
      const name = ctx.resolveCollectionName?.(collectionId)
      const label =
        event.type === EventType.WORKSPACE_CREATED
          ? activityLabels.newCollection(
              name ?? (typeof payload.name === 'string' ? payload.name : undefined),
            )
          : activityLabels.collectionChanges(name)
      upsert(`collection:${collectionId}`, 'collection', label).eventClientIds.push(event.clientId)
      continue
    }
    if (ROUTE_TYPES.has(event.type)) {
      upsert('route-changes', 'route-changes', '').eventClientIds.push(event.clientId)
      continue
    }

    // 4. Catch-all — the list must always sum to the queue
    upsert('other', 'other', '').eventClientIds.push(event.clientId)
  }

  // Finalize count-dependent labels and status
  return items.map((item) => {
    const count = item.eventClientIds.length
    let label = item.label
    if (item.kind === 'photos') {
      const collectionId = item.key.slice('photos:'.length)
      label = activityLabels.photos(count, ctx.resolveCollectionName?.(collectionId))
    } else if (item.kind === 'photo-changes') {
      label = activityLabels.photoChanges(count)
    } else if (item.kind === 'places') {
      label = activityLabels.places(count)
    } else if (item.kind === 'route-changes') {
      label = activityLabels.routeChanges(count)
    } else if (item.kind === 'other') {
      label = activityLabels.otherChanges(count)
    }

    const status = item.eventClientIds.some((id) => ctx.failedEventIds?.has(id))
      ? ('problem' as const)
      : item.eventClientIds.some((id) => ctx.inFlightEventIds?.has(id))
        ? ('syncing' as const)
        : ('waiting' as const)

    return {
      key: item.key,
      kind: item.kind,
      label,
      count,
      eventClientIds: item.eventClientIds,
      status,
    }
  })
}
